import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';

const productsCollection = collection(db, 'products');
const archivedProductsCollection = collection(db, 'archived_products');

/**
 * Clean up archived products by removing the archived flag
 * Since we can't create new collections due to permissions, we'll clean up the existing data
 */
export async function migrateArchivedProducts() {
  try {
    console.log('🔄 [Migration] Starting archived products cleanup...');
    
    // Find all products marked as archived
    const archivedQuery = query(productsCollection, where('archived', '==', true));
    const snapshot = await getDocs(archivedQuery);
    
    console.log(`🔄 [Migration] Found ${snapshot.docs.length} archived products to clean up`);
    
    if (snapshot.docs.length === 0) {
      console.log('✅ [Migration] No archived products found to clean up');
      return {
        success: true,
        migrated: 0,
        errors: []
      };
    }
    
    const results = {
      success: true,
      migrated: 0,
      errors: []
    };
    
    // Process each archived product
    for (const docSnapshot of snapshot.docs) {
      try {
        const productData = docSnapshot.data();
        const productId = docSnapshot.id;
        
        console.log(`🔄 [Migration] Processing archived product: ${productId}`);
        
        // Remove the archived flag and related fields to clean up the data
        const cleanupData = {
          archived: false,
          archived_at: null,
          archived_by: null,
          archived_reason: null
        };
        
        // Update the product to remove archived status
        console.log(`🔍 [Migration] Cleaning up archived product: ${productId}`);
        await updateDoc(doc(productsCollection, productId), cleanupData);
        console.log(`✅ [Migration] Successfully cleaned up product: ${productId}`);
        
        results.migrated++;
        
      } catch (error) {
        console.error(`❌ [Migration] Error cleaning up product ${docSnapshot.id}:`, error);
        results.errors.push({
          productId: docSnapshot.id,
          error: error.message
        });
        results.success = false;
      }
    }
    
    console.log(`✅ [Migration] Cleanup completed. Cleaned: ${results.migrated}, Errors: ${results.errors.length}`);
    return results;
    
  } catch (error) {
    console.error('❌ [Migration] Migration failed:', error);
    throw error;
  }
}

/**
 * Check migration status - count archived products in the products collection
 */
export async function checkMigrationStatus() {
  try {
    console.log('🔍 [Migration] Checking migration status...');
    
    // Count archived products in original collection
    const archivedQuery = query(productsCollection, where('archived', '==', true));
    const originalSnapshot = await getDocs(archivedQuery);
    const originalCount = originalSnapshot.docs.length;
    
    console.log(`🔍 [Migration] Status - Archived products in collection: ${originalCount}`);
    
    return {
      originalArchived: originalCount,
      archivedCollection: 0, // We're not using a separate collection anymore
      needsMigration: originalCount > 0
    };
    
  } catch (error) {
    console.error('❌ [Migration] Error checking migration status:', error);
    throw error;
  }
}

/**
 * Validate migration - ensure all archived products have been cleaned up
 */
export async function validateMigration() {
  try {
    console.log('🔍 [Migration] Validating migration...');
    
    const status = await checkMigrationStatus();
    
    if (status.originalArchived > 0) {
      console.log(`⚠️ [Migration] Found ${status.originalArchived} products still marked as archived`);
      return {
        valid: false,
        message: `${status.originalArchived} products still need cleanup`,
        status
      };
    }
    
    if (status.originalArchived === 0) {
      console.log('✅ [Migration] No archived products found - cleanup successful');
      return {
        valid: true,
        message: 'All archived products have been cleaned up successfully',
        status
      };
    }
    
    console.log('✅ [Migration] Migration validation successful');
    return {
      valid: true,
      message: 'Migration validation completed',
      status
    };
    
  } catch (error) {
    console.error('❌ [Migration] Error validating migration:', error);
    throw error;
  }
}
